import { collection, doc, getDoc, getDocs, updateDoc, deleteDoc, setDoc, query, where, orderBy } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';

const PRODUCTS_COLLECTION = 'storeProducts';
const ORDERS_COLLECTION = 'storeOrders';

const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
const asUuid = (v) => (typeof v === 'string' && UUID_RE.test(v) ? v : null);

// Enums
export const ProductSize = {
  XS: 'XS',
  S: 'S',
  M: 'M',
  L: 'L',
  XL: 'XL',
  XXL: 'XXL',
};

export const UniformSubcategory = {
  SHIRT: 'Shirt',
  PANTS: 'Pants',
  PE_SHIRT: 'PE Shirt',
  PE_SKIRT: 'PE Skirt',
  DRESS: 'Dress',
  JACKET: 'Jacket',
  OTHER: 'Other',
};

export const ProductCategory = {
  UNIFORM: 'Uniform',
  BOOKS: 'Books',
  CRAFTS: 'Crafts',
  ACCESSORIES: 'Accessories',
  STATIONERY: 'Stationery',
  OTHER: 'Other',
};

export const storeService = {
  // Products
  async getActiveProducts(schoolId = null) {
    const q = collection(db, PRODUCTS_COLLECTION);
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(d => {
        if (!schoolId) return true;
        const ids = (Array.isArray(schoolId) ? schoolId : [schoolId]).map(v => String(v).toLowerCase());
        return ids.includes((d.schoolId || '').toLowerCase());
      });
  },

  async getAllProducts(schoolId = null) {
    const q = collection(db, PRODUCTS_COLLECTION);
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(d => {
        if (!schoolId) return true;
        const ids = (Array.isArray(schoolId) ? schoolId : [schoolId]).map(v => String(v).toLowerCase());
        return ids.includes((d.schoolId || '').toLowerCase());
      });
  },

  async saveProduct(product) {
    const productData = {
      // iOS StoreProduct decodes these as non-optional — always write them.
      name: product.name || '',
      description: product.description || '',
      category: product.category || 'Other',
      price: typeof product.price === 'number' ? product.price : (parseFloat(product.price) || 0),
      stockQuantity: typeof product.stockQuantity === 'number' ? product.stockQuantity : (parseInt(product.stockQuantity) || 0),
      isActive: product.isActive !== false,
      ...product,
      updatedAt: new Date(),
    };
    if (product.id) {
      await updateDoc(doc(db, PRODUCTS_COLLECTION, product.id), productData);
      return product.id;
    } else {
      // iOS requires `id` decodable as a UUID — key the doc by a fresh UUID
      // (same convention as iOS saveProduct) instead of an addDoc auto-id.
      const productId = crypto.randomUUID();
      productData.id = productId;
      productData.createdAt = new Date();
      await setDoc(doc(db, PRODUCTS_COLLECTION, productId), productData);
      return productId;
    }
  },

  async deleteProduct(productId) {
    await deleteDoc(doc(db, PRODUCTS_COLLECTION, productId));
  },

  // Image Upload
  async uploadProductImage(file, productId) {
    const fileExt = file.name.split('.').pop().toLowerCase();
    const allowedTypes = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
    if (!allowedTypes.includes(fileExt)) {
      throw new Error('Invalid file type. Only JPG, PNG, GIF, and WebP are allowed.');
    }
    if (file.size > 5 * 1024 * 1024) {
      throw new Error('Image must be less than 5MB.');
    }
    const imageRef = ref(storage, `product_images/${productId}.${fileExt}`);
    const metadata = { contentType: file.type };
    await uploadBytes(imageRef, file, metadata);
    const url = await getDownloadURL(imageRef);
    return url;
  },

  async deleteProductImage(imageUrl) {
    try {
      const imageRef = ref(storage, imageUrl);
      await deleteObject(imageRef);
    } catch (e) {
      console.warn('Could not delete old image:', e);
    }
  },

  // Orders
  async getAllOrders(schoolId = null) {
    const q = query(collection(db, ORDERS_COLLECTION), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(d => {
        if (!schoolId) return true;
        const ids = (Array.isArray(schoolId) ? schoolId : [schoolId]).map(v => String(v).toLowerCase());
        return ids.includes((d.schoolId || '').toLowerCase());
      });
  },

  async getOrdersByParent(parentId) {
    const q = query(collection(db, ORDERS_COLLECTION), where('parentId', '==', parentId), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  },

  async getOrdersByStudent(studentId) {
    const q = query(collection(db, ORDERS_COLLECTION), where('studentId', '==', studentId), orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  },

  async placeOrder(order) {
    const orderId = crypto.randomUUID();
    const now = new Date();

    // iOS decodes storeOrders via doc.data(as: StoreOrder) — every item needs
    // `id` + `productId` decodable as UUID, plus productName/price/quantity.
    // createdAt/updatedAt must be real Firestore Timestamps (JS Date) — the
    // Firestore decoder cannot decode a String into a Date there.
    const items = [];
    for (const item of (order.items || [])) {
      const productRef = doc(db, PRODUCTS_COLLECTION, item.productId);
      const productSnap = await getDoc(productRef);
      const pdata = productSnap.exists() ? productSnap.data() : null;
      const productUuid = asUuid(pdata?.id) || asUuid(item.productId);
      const qty = typeof item.quantity === 'number' ? item.quantity : 1;
      const price = typeof item.price === 'number'
        ? item.price
        : (typeof pdata?.price === 'number' ? pdata.price
          : (item.subtotal && qty ? item.subtotal / qty : 0));
      items.push({
        id: crypto.randomUUID(),
        productId: productUuid || String(item.productId || ''),
        productDocId: item.productId || null,
        productName: item.productName || pdata?.name || '',
        price: price,
        quantity: qty,
      });

      // Reduce stock using the actual product document key
      if (productSnap.exists()) {
        const newStock = Math.max(0, (pdata.stockQuantity || 0) - qty);
        await updateDoc(productRef, { stockQuantity: newStock, updatedAt: new Date() });
      }
    }

    const orderData = {
      ...order,
      id: orderId,
      items,
      status: order.status || 'Pending',
      paymentMethod: order.paymentMethod || 'Cash',
      totalAmount: typeof order.totalAmount === 'number'
        ? order.totalAmount
        : items.reduce((sum, i) => sum + i.price * i.quantity, 0),
      createdAt: now,
      updatedAt: now,
    };
    await setDoc(doc(db, ORDERS_COLLECTION, orderId), orderData);
    return orderId;
  },

  async updateOrderStatus(orderId, status) {
    await updateDoc(doc(db, ORDERS_COLLECTION, orderId), { status, updatedAt: new Date() });
  },

  // Analytics
  async getStoreAnalytics(schoolId = null) {
    const q = collection(db, ORDERS_COLLECTION);
    const snapshot = await getDocs(q);
    const orders = snapshot.docs
      .map(d => ({ id: d.id, ...d.data() }))
      .filter(d => {
        if (!schoolId) return true;
        const ids = (Array.isArray(schoolId) ? schoolId : [schoolId]).map(v => String(v).toLowerCase());
        return ids.includes((d.schoolId || '').toLowerCase());
      });
    const completed = orders.filter(o => o.status === 'Completed');
    const totalRevenue = completed.reduce((sum, o) => sum + (o.totalAmount || 0), 0);
    const totalOrders = orders.length;
    const pendingOrders = orders.filter(o => o.status === 'Pending').length;
    const completedOrders = completed.length;

    const monthlyRevenue = {};
    completed.forEach(order => {
      const date = order.createdAt?.toDate ? order.createdAt.toDate() : new Date(order.createdAt);
      const monthKey = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      monthlyRevenue[monthKey] = (monthlyRevenue[monthKey] || 0) + (order.totalAmount || 0);
    });

    return { totalRevenue, totalOrders, pendingOrders, completedOrders, monthlyRevenue };
  },
};
